import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import "react-toastify/dist/ReactToastify.css";
import { Container, Input } from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import { RefreshCw, Save, Upload, Trash2 } from "react-feather";
import { useSelector } from "react-redux";
import Breadcrumb from "../../common/breadcrumb";
import {
  getProductTemplateListing,
  updateProductTemplate,
  deleteProductTemplateImage,
} from "../../../utils/apiCalls";
import { errorToast } from "../../../shared/Toast";
import ToggleSwitch from "../../../shared/ToggleSwitch";
import { getProductImageUrl, FALLBACK_IMAGE } from "../../../utils/imageUtils";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Tile, RCard, Loader as RLoader } from "../../reporting/ReportingShared";

const ProductTemplates = () => {
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? merchantId : reduxSelectedMerchant;

  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const fileInputs = useRef({});

  const loadTemplates = useCallback(async () => {
    if (!selectedMerchant || selectedMerchant === "all") {
      setTemplates([]);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getProductTemplateListing(selectedMerchant, { search }, 1, 1000);
      const list = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
      setTemplates(list);
    } catch (e) {
      errorToast("Failed to load product templates");
      setTemplates([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedMerchant, search]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const getDraft = (tpl) =>
    editing[tpl.id] ?? {
      arabicName: tpl.arabicName ?? "",
      priority: tpl.priority ?? 100,
      isActive: !!tpl.isActive,
      image: null,
      imagePreview: null,
    };

  const isDirty = (tpl) => {
    const draft = editing[tpl.id];
    if (!draft) return false;
    return (
      (draft.arabicName ?? "") !== (tpl.arabicName ?? "") ||
      Number(draft.priority) !== Number(tpl.priority ?? 100) ||
      draft.isActive !== !!tpl.isActive ||
      !!draft.image
    );
  };

  const updateDraft = (tpl, patch) => {
    setEditing((prev) => ({
      ...prev,
      [tpl.id]: { ...getDraft(tpl), ...patch },
    }));
  };

  const onPickFile = (tpl, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    updateDraft(tpl, { image: file, imagePreview: previewUrl });
  };

  const clearDraftImage = (tpl) => {
    const draft = editing[tpl.id];
    if (draft?.imagePreview) URL.revokeObjectURL(draft.imagePreview);
    updateDraft(tpl, { image: null, imagePreview: null });
  };

  const handleSave = async (tpl) => {
    const draft = getDraft(tpl);
    setSavingId(tpl.id);
    try {
      const payload = {
        arabicName: draft.arabicName ?? "",
        priority: Number(draft.priority) || 0,
        isActive: !!draft.isActive,
      };
      if (draft.image) payload.image = draft.image;

      const result = await updateProductTemplate(tpl.id, payload);
      if (result) {
        toast.success("Template updated");
        if (draft.imagePreview) URL.revokeObjectURL(draft.imagePreview);
        setEditing((prev) => {
          const next = { ...prev };
          delete next[tpl.id];
          return next;
        });
        await loadTemplates();
      }
    } catch (e) {
      errorToast(e?.message || "Failed to update template");
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteImage = async (tpl) => {
    if (!tpl.image1) return;
    if (!window.confirm("Remove uploaded image for this template?")) return;
    const ok = await deleteProductTemplateImage(tpl.id);
    if (ok) await loadTemplates();
  };

  const totalCount = templates.length;
  const activeCount = templates.filter((t) => t.isActive).length;
  const inactiveCount = totalCount - activeCount;
  const withImageCount = templates.filter((t) => t.image1 || t.merchant_image).length;

  return (
    <Fragment>
      <ToastContainer />
      <Breadcrumb title="Product Templates" parent="Product" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          {/* KPI Tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 24 }}>
            <Tile label="Total Templates" value={totalCount} icon="🗂️" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
            <Tile label="Active" value={activeCount} icon="✅" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={60} />
            <Tile label="Inactive" value={inactiveCount} icon="🚫" gradient={`linear-gradient(135deg, #E74C3C, #FF6B6B)`} delay={120} />
            <Tile label="With Image" value={withImageCount} icon="🖼️" gradient={`linear-gradient(135deg, #4FACFE, #00F2FE)`} delay={180} />
          </div>

          <RCard>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
              <div
                onClick={loadTemplates}
                style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", background: C.white, flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.coral; e.currentTarget.style.background = C.coralLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.white; }}
              >
                <RefreshCw size={15} color={C.textMed} />
              </div>

              <Input
                placeholder="Search by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ maxWidth: 260, borderRadius: 10, fontSize: 13 }}
              />

              <div style={{ fontSize: 12, color: C.textLight, marginLeft: 8 }}>
                Templates are synced from POS. Upload an image and add an Arabic name to enhance customer apps.
              </div>

              <span style={{ marginLeft: "auto", fontSize: 12, color: C.textLight, background: C.bg, padding: "4px 12px", borderRadius: 20 }}>
                {totalCount} templates
              </span>
            </div>

            {isLoading ? <RLoader /> : (!selectedMerchant || selectedMerchant === "all") ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textLight }}>Select a merchant to view product templates.</div>
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={{ ...tbl.th, width: 80 }}>Image</th>
                      <th style={tbl.th}>Name</th>
                      <th style={tbl.th}>Arabic Name</th>
                      <th style={{ ...tbl.th, width: 100 }}>Products</th>
                      <th style={{ ...tbl.th, width: 110 }}>Priority</th>
                      <th style={{ ...tbl.th, textAlign: "center", width: 110 }}>Active</th>
                      <th style={{ ...tbl.th, textAlign: "center", width: 240 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.length > 0 ? templates.map((tpl) => {
                      const draft = getDraft(tpl);
                      const dirty = isDirty(tpl);
                      const saving = savingId === tpl.id;
                      const previewSrc = draft.imagePreview || getProductImageUrl(tpl);
                      return (
                        <tr key={tpl.id} className="rpt-row">
                          <td style={tbl.td}>
                            <img
                              alt=""
                              src={previewSrc}
                              onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                              style={{ width: 50, height: 50, borderRadius: 6, objectFit: "cover", border: `1px solid ${C.border}` }}
                            />
                          </td>
                          <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>
                            {tpl.title || "—"}
                            <div style={{ fontSize: 11, color: C.textLight, fontWeight: 500, marginTop: 2 }}>
                              POS id: {tpl.merchant_template_id}
                            </div>
                          </td>
                          <td style={tbl.td}>
                            <Input
                              type="text"
                              dir="rtl"
                              value={draft.arabicName}
                              onChange={(e) => updateDraft(tpl, { arabicName: e.target.value })}
                              style={{ borderRadius: 8, fontSize: 13 }}
                            />
                          </td>
                          <td style={{ ...tbl.td, textAlign: "center", color: C.textMed, fontWeight: 600 }}>
                            {tpl.productCount ?? 0}
                          </td>
                          <td style={tbl.td}>
                            <Input
                              type="number"
                              value={draft.priority}
                              onChange={(e) => updateDraft(tpl, { priority: e.target.value })}
                              style={{ maxWidth: 90, borderRadius: 8, fontSize: 13 }}
                            />
                          </td>
                          <td style={{ ...tbl.td, textAlign: "center" }}>
                            <div style={{ display: "inline-block" }}>
                              <ToggleSwitch
                                label=""
                                checked={draft.isActive}
                                handleToggle={(checked) => updateDraft(tpl, { isActive: checked })}
                              />
                            </div>
                          </td>
                          <td style={{ ...tbl.td, textAlign: "center" }}>
                            <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                              <input
                                type="file"
                                accept="image/*"
                                ref={(el) => { fileInputs.current[tpl.id] = el; }}
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  onPickFile(tpl, f);
                                  e.target.value = "";
                                }}
                              />
                              {draft.image ? (
                                <button
                                  onClick={() => clearDraftImage(tpl)}
                                  title="Discard new image"
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.textMed, fontSize: 12, cursor: "pointer" }}
                                >
                                  <Trash2 size={13} /> Discard
                                </button>
                              ) : (
                                <button
                                  onClick={() => fileInputs.current[tpl.id]?.click()}
                                  title="Upload image"
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.textMed, fontSize: 12, cursor: "pointer" }}
                                >
                                  <Upload size={13} /> Image
                                </button>
                              )}

                              {tpl.image1 && !draft.image && (
                                <button
                                  onClick={() => handleDeleteImage(tpl)}
                                  title="Remove existing image"
                                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: "#E74C3C", fontSize: 12, cursor: "pointer" }}
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}

                              <button
                                onClick={() => handleSave(tpl)}
                                disabled={!dirty || saving}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "6px 14px",
                                  borderRadius: 8,
                                  border: "none",
                                  background: dirty ? C.coral : C.border,
                                  color: dirty ? C.white : C.textLight,
                                  fontWeight: 700,
                                  fontSize: 12,
                                  cursor: dirty && !saving ? "pointer" : "not-allowed",
                                  transition: "all 0.15s",
                                }}
                              >
                                <Save size={13} />
                                {saving ? "Saving" : "Save"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr><td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>No product templates found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>
        </div>
      </Container>
    </Fragment>
  );
};

export default ProductTemplates;
